// Персональный размер текста интерфейса.
//
// Значение хранится на сервере в account_settings (ключ interface.text-size) — поэтому
// выбор переезжает с человеком на другое устройство. Применяется атрибутом data-text-size
// на <html>; шкалу, которую он сдвигает, описывает scss/_root.scss.
//
// Локальная копия в localStorage — только кэш для первой отрисовки: main.js применяет её
// синхронно, до монтирования приложения, и экран не перестраивается, когда приходит
// ответ сервера. Источник правды — сервер: при загрузке (store.js, bootVuex) его значение
// перекрывает кэш, в том числе на устройстве, где человек раньше выбирал другое.
import userSettings from './userSettings';

export const TEXT_SIZE_SETTING_KEY = 'interface.text-size';

export const TEXT_SIZES = {
    COMPACT: 'compact',
    STANDARD: 'standard',
    LARGE: 'large',
};

export const DEFAULT_TEXT_SIZE = TEXT_SIZES.STANDARD;

export const TEXT_SIZE_OPTIONS = [
    { value: TEXT_SIZES.COMPACT, label: 'Компактный', hint: '13 px' },
    { value: TEXT_SIZES.STANDARD, label: 'Стандартный', hint: '14 px' },
    { value: TEXT_SIZES.LARGE, label: 'Крупный', hint: '16 px' },
];

const STORAGE_KEY = 'textSize';
const ATTRIBUTE = 'data-text-size';

const KNOWN = Object.values(TEXT_SIZES);

// Отсутствующее, устаревшее или чужое значение — стандартный размер.
export function normalizeTextSize(value) {
    return KNOWN.includes(value) ? value : DEFAULT_TEXT_SIZE;
}

// Стандартному размеру атрибут не нужен: шкала по умолчанию уже стандартная.
export function applyTextSize(value, root = document.documentElement) {
    const size = normalizeTextSize(value);

    if (size === DEFAULT_TEXT_SIZE) {
        root.removeAttribute(ATTRIBUTE);
    } else {
        root.setAttribute(ATTRIBUTE, size);
    }

    return size;
}

// localStorage может быть недоступен (приватный режим, запрет политики) — тогда
// размер просто не переживёт перезагрузку до ответа сервера, это не ошибка.
export function rememberTextSize(value, storage = window.localStorage) {
    try {
        storage.setItem(STORAGE_KEY, normalizeTextSize(value));
    } catch (error) {
        //
    }
}

export function readCachedTextSize(storage = window.localStorage) {
    try {
        return normalizeTextSize(storage.getItem(STORAGE_KEY));
    } catch (error) {
        return DEFAULT_TEXT_SIZE;
    }
}

export function restoreCachedTextSize(root = document.documentElement, storage = window.localStorage) {
    return applyTextSize(readCachedTextSize(storage), root);
}

// Сколько загрузка приложения готова ждать размер текста. У http нет общего таймаута,
// а зависший запрос настроек не должен держать весь интерфейс за спиннером.
export const TEXT_SIZE_SYNC_TIMEOUT = 3000;

// Не бросает и не ждёт дольше timeout: без ответа сервера остаётся размер из кэша.
// Опоздавший ответ всё равно применяется и запоминается, когда придёт.
export function syncTextSizeFromServer(
    settings = userSettings,
    root = document.documentElement,
    storage = window.localStorage,
    timeout = TEXT_SIZE_SYNC_TIMEOUT,
) {
    const request = settings
        .getAsync(TEXT_SIZE_SETTING_KEY)
        .then((value) => {
            const size = applyTextSize(value, root);
            rememberTextSize(size, storage);

            return size;
        })
        .catch(() => readCachedTextSize(storage));

    const fallback = new Promise((resolve) => setTimeout(() => resolve(readCachedTextSize(storage)), timeout));

    return Promise.race([request, fallback]);
}

export function textSizeSetting(value) {
    return { key: TEXT_SIZE_SETTING_KEY, value: normalizeTextSize(value), type: 'string' };
}
